import { useCheckPermission } from 'hooks/useCheckPermission'
import { SelectOption } from 'components/Selector'
import { useGlobalState } from 'context/GlobalStateContext'
import { useCallback, useEffect, useState } from 'react'
import {
  usePdfDesignLayerAsphaltMaterialsQuery,
  usePdfDesignLayerConcreteMaterialsQuery,
  usePdfDesignLayerDailyInspectionWorkTypesQuery,
  usePdfDesignLayerMaterialRequirementsQuery,
  usePdfDesignLayerAsphaltPlacementsQuery,
  usePdfDesignLayerConcretePlacementsQuery,
  usePdfDesignLayerLotsQuery,
  usePdfDesignLayerDailyInspectionControlPointsQuery,
  usePdfDesignLayerDailyInspectionInspectionsQuery,
  usePdfDesignLayerDailyInspectionPunchListsQuery,
  ConcretePlacement,
  AsphaltPlacement,
  Lot,
  DailyInspectionControlPoint,
  DailyInspectionPunchList,
  DailyInspectionInspection,
} from 'schema'
import { an, BetterEnum, Permission, Report, ReportItem } from 'shared'
import { DefaultQueryHookOptions } from '../../../../../../../../../utils/defaults'

export const DesignLayerOption = {
  AsphaltMaterials: { name: 'Asphalt Materials', featureServerId: 45 },
  WorkTypes: { name: 'Work Types', featureServerId: 31 },
  MaterialRequirements: { name: 'Material Requirements', featureServerId: 8 },
  ConcreteMaterials: { name: 'Concrete Materials', featureServerId: 18 },
} as const
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type DesignLayerOption = BetterEnum<typeof DesignLayerOption>

export type PreviewOptionTypename = (
  | ConcretePlacement
  | AsphaltPlacement
  | Lot
  | DailyInspectionControlPoint
  | DailyInspectionPunchList
  | DailyInspectionInspection
)['__typename']
interface PreviewOption {
  /**
   * A list of globalId/values for this Preview Option
   */
  options: SelectOption[]
  /**
   * the associated Layer needed to query the server
   */
  option: Pick<
    | ConcretePlacement
    | AsphaltPlacement
    | Lot
    | DailyInspectionControlPoint
    | DailyInspectionPunchList
    | DailyInspectionInspection,
    '__typename'
  > & { name: string }
}

export const useDesignLayers = ({
  featureServerId,
  name,
}: DesignLayerOption) => {
  const {
    state: { projectGlobalId },
  } = useGlobalState()

  const byId = () => ({
    variables: { projectId: projectGlobalId },
    ...DefaultQueryHookOptions,
  })
  const asphaltMaterials = usePdfDesignLayerAsphaltMaterialsQuery({
    ...byId(),
    skip:
      featureServerId !== DesignLayerOption.AsphaltMaterials.featureServerId,
  })
  const concreteMaterials = usePdfDesignLayerConcreteMaterialsQuery({
    ...byId(),
    skip:
      featureServerId !== DesignLayerOption.ConcreteMaterials.featureServerId,
  })
  const dailyInspectionWorkTypes =
    usePdfDesignLayerDailyInspectionWorkTypesQuery({
      ...byId(),
      skip: featureServerId !== DesignLayerOption.WorkTypes.featureServerId,
    })
  const materialRequirements = usePdfDesignLayerMaterialRequirementsQuery({
    ...byId(),
    skip:
      featureServerId !==
      DesignLayerOption.MaterialRequirements.featureServerId,
  })

  const [placeholder, setPlaceholder] = useState<string>()
  useEffect(() => {
    setPlaceholder(`Select ${an(name).slice(0, -1)}`)
  }, [name])

  const [options, setOptions] = useState<SelectOption[]>([])
  useEffect(() => {
    let options: SelectOption[] | undefined
    if (
      featureServerId === DesignLayerOption.AsphaltMaterials.featureServerId
    ) {
      options = asphaltMaterials.data?.asphaltMaterials?.records?.map(
        ({ globalId: value, placementType: label }) => ({ value, label })
      )
    } else if (
      featureServerId === DesignLayerOption.ConcreteMaterials.featureServerId
    )
      options = concreteMaterials.data?.concreteMaterials?.records?.map(
        ({ globalId: value, placementType: label }) => ({ label, value })
      )
    else if (
      featureServerId === DesignLayerOption.MaterialRequirements.featureServerId
    )
      options = materialRequirements.data?.materialRequirements?.records?.map(
        ({ globalId: value, materialSection: label }) => ({ label, value })
      )
    else if (featureServerId === DesignLayerOption.WorkTypes.featureServerId)
      options =
        dailyInspectionWorkTypes.data?.dailyInspectionWorkTypes?.records?.map(
          ({ globalId: value, name: label }) => ({ label, value })
        )
    setOptions(options ?? [])
  }, [
    asphaltMaterials.data?.asphaltMaterials?.records,
    concreteMaterials.data?.concreteMaterials?.records,
    dailyInspectionWorkTypes.data?.dailyInspectionWorkTypes?.records,
    featureServerId,
    materialRequirements.data?.materialRequirements?.records,
  ])

  const asphaltPlacements = usePdfDesignLayerAsphaltPlacementsQuery({
    ...byId(),
    skip: !(
      featureServerId === DesignLayerOption.AsphaltMaterials.featureServerId
    ),
  })
  const concretePlacements = usePdfDesignLayerConcretePlacementsQuery({
    ...byId(),
    skip: !(
      featureServerId === DesignLayerOption.ConcreteMaterials.featureServerId
    ),
  })
  const lots = usePdfDesignLayerLotsQuery({
    ...byId(),
    skip: !(
      featureServerId === DesignLayerOption.MaterialRequirements.featureServerId
    ),
  })
  const controlPoints = usePdfDesignLayerDailyInspectionControlPointsQuery({
    ...byId(),
    skip: !(featureServerId === DesignLayerOption.WorkTypes.featureServerId),
  })
  const inspections = usePdfDesignLayerDailyInspectionInspectionsQuery({
    ...byId(),
    skip: !(featureServerId === DesignLayerOption.WorkTypes.featureServerId),
  })
  const punchLists = usePdfDesignLayerDailyInspectionPunchListsQuery({
    ...byId(),
    skip: !(featureServerId === DesignLayerOption.WorkTypes.featureServerId),
  })

  const [previewOptions, setPreviewOptions] = useState<PreviewOption[]>()
  useEffect(() => {
    if (featureServerId === DesignLayerOption.AsphaltMaterials.featureServerId)
      setPreviewOptions([
        {
          option: { __typename: 'AsphaltPlacement', name: 'Asphalt Placement' },
          options:
            asphaltPlacements.data?.asphaltPlacements?.records?.map(
              ({ globalId: value, placementId: label }) => ({ value, label })
            ) ?? [],
        },
      ])
    else if (
      featureServerId === DesignLayerOption.ConcreteMaterials.featureServerId
    )
      setPreviewOptions([
        {
          option: {
            __typename: 'ConcretePlacement',
            name: 'Concrete Placement',
          },
          options:
            concretePlacements.data?.concretePlacements?.records?.map(
              ({ globalId: value, placementId: label }) => ({ value, label })
            ) ?? [],
        },
      ])
    else if (
      featureServerId === DesignLayerOption.MaterialRequirements.featureServerId
    )
      setPreviewOptions([
        {
          option: { __typename: 'Lot', name: 'Density Lot' },
          options:
            lots.data?.lots?.records?.map(
              ({ globalId: value, lotId: label }) => ({ label, value })
            ) ?? [],
        },
      ])
    else if (featureServerId === DesignLayerOption.WorkTypes.featureServerId)
      setPreviewOptions([
        {
          option: {
            __typename: 'DailyInspectionPunchList',
            name: 'Punch List',
          },
          options:
            punchLists.data?.dailyInspectionPunchLists?.records?.map(
              ({ globalId: value, punchListId: label }) => ({ label, value })
            ) ?? [],
        },
        {
          option: {
            __typename: 'DailyInspectionControlPoint',
            name: 'Control Point',
          },
          options:
            controlPoints.data?.dailyInspectionControlPoints?.records?.map(
              ({ globalId: value, controlPointId: label }) => ({ label, value })
            ) ?? [],
        },
        {
          option: {
            __typename: 'DailyInspectionInspection',
            name: 'Inspection',
          },
          options:
            inspections.data?.dailyInspectionInspections?.records?.map(
              ({ globalId: value, inspectionId: label }) => ({ label, value })
            ) ?? [],
        },
      ])
    else setPreviewOptions([])
  }, [
    asphaltPlacements.data?.asphaltPlacements?.records,
    concretePlacements.data?.concretePlacements?.records,
    controlPoints.data?.dailyInspectionControlPoints?.records,
    featureServerId,
    inspections.data?.dailyInspectionInspections?.records,
    lots.data?.lots?.records,
    punchLists.data?.dailyInspectionPunchLists?.records,
  ])

  const [reports, setReports] = useState<ReportItem[]>([])
  useEffect(() => {
    if (featureServerId === DesignLayerOption.AsphaltMaterials.featureServerId)
      setReports([Report.AsphaltCoreSheet, Report.AsphaltLot])
    else if (
      featureServerId === DesignLayerOption.ConcreteMaterials.featureServerId
    )
      setReports([Report.ConcreteTestSet, Report.ConcretePlacementCoreSheet])
    else if (
      featureServerId === DesignLayerOption.MaterialRequirements.featureServerId
    )
      setReports([Report.DensityLot])
    else if (featureServerId === DesignLayerOption.WorkTypes.featureServerId)
      setReports([Report.Inspection, Report.ControlPoint, Report.PunchList])
  }, [featureServerId])

  const canUpdate = useCheckPermission(Permission.EditPDFDesignLayer)
  const selectOptionMessage = useCallback(
    (options?: { preview?: boolean }) => {
      const { preview } = options ?? {}
      const base = `${placeholder}, or enable "Use with all ${name}"`
      const _preview = 'to generate a preview'
      if (preview) {
        if (canUpdate) return `${base} ${_preview}`
        return `${placeholder} to view configured project feature layers`
      } else {
        if (canUpdate) return `${base} to configure project feature layers`
        return `${base} ${_preview}`
      }
    },
    [canUpdate, name, placeholder]
  )

  const [loading, setLoading] = useState(false)
  useEffect(() => {
    setLoading(
      asphaltPlacements.loading ||
        concretePlacements.loading ||
        controlPoints.loading ||
        inspections.loading ||
        lots.loading ||
        punchLists.loading
    )
  }, [
    asphaltPlacements.loading,
    concretePlacements.loading,
    controlPoints.loading,
    inspections.loading,
    lots.loading,
    punchLists.loading,
  ])

  return {
    reports,
    placeholder,
    selectOptionMessage,
    options,
    loading,
    previewOptions,
  }
}
